/**
 * Safe serialization for JSON-LD embedded in an inline
 * `<script type="application/ld+json">` via `dangerouslySetInnerHTML`.
 *
 * Plain `JSON.stringify` is unsafe in an inline `<script>` context: if
 * any string value contains the sequence `</script>` (or `<!--`, or a
 * lone `<`), the browser's HTML parser terminates the script element
 * early, letting attacker-controllable field content — a blog/solution
 * title, description, tag, FAQ answer, or a raw `jsonLdOverrides` object,
 * all authored through the CMS — break out into live markup. That is a
 * stored-XSS sink.
 *
 * The fix: escape the HTML-significant characters (`<`, `>`, `&`) as
 * their JSON `\uXXXX` unicode escapes. These are semantically identical
 * once parsed as JSON — a `ld+json` script is read as structured data by
 * crawlers, never executed as JavaScript — but the HTML parser never
 * sees a literal `<`, `>`, or `&`, so it cannot be tricked into closing
 * the `<script>` tag early.
 *
 * 2026-06 security audit: every JSON-LD `<script>` on the public
 * blog/solutions pages used raw `JSON.stringify`. They now route here.
 */
export function jsonLdSafe(data: unknown): string {
  return JSON.stringify(data).replace(
    /[<>&]/g,
    (ch) => "\\u" + ch.charCodeAt(0).toString(16).padStart(4, "0"),
  );
}

/**
 * Stable `@id`s for the single Organization + WebSite entity nodes,
 * defined once in `(marketing)/layout.tsx` so they render on every
 * marketing page. Other pages/components reference the entity by
 * `{ "@id": ORGANIZATION_ID }` instead of rebuilding an inline
 * publisher/provider Organization — see CLAUDE.md's JSON-LD notes and
 * issue #1251.
 */
export const ORGANIZATION_ID = "https://raileon.com/#organization";
export const WEBSITE_ID = "https://raileon.com/#website";

/**
 * The site's one Organization node. Fields are sourced only from
 * copy that already exists elsewhere on the site — legalName and
 * address from `/terms`, founder from `/about` — never invented for
 * this graph. `logo` still points at the favicon: `frontend/public/`
 * has no real wordmark asset yet (tracked as a manual step on
 * issue #1251), so this intentionally does not satisfy a "non-favicon
 * logo" audit until that asset exists.
 */
export const organizationJsonLd = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Raileon",
  legalName: "Raileon LLC",
  url: "https://raileon.com",
  logo: "https://raileon.com/favicon-256.png",
  email: "admin@raileon.com",
  description:
    "AI implementation agency and white-labeled SaaS platform that gives businesses access to intelligent AI agents for marketing, outreach, and operations.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Miami",
    addressRegion: "FL",
    addressCountry: "US",
  },
  founder: {
    "@type": "Person",
    name: "Konstantin Gore",
    jobTitle: "Founder & CEO",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: "admin@raileon.com",
      contactType: "customer support",
    },
    {
      "@type": "ContactPoint",
      email: "security@raileon.com",
      contactType: "security",
    },
    {
      "@type": "ContactPoint",
      email: "press@raileon.com",
      contactType: "media relations",
    },
  ],
  sameAs: [
    "https://x.com/RaileonAi",
    "https://www.instagram.com/raileonai",
    "https://www.tiktok.com/@raileonai",
    "https://github.com/SkyRaileon",
  ],
};

export const websiteJsonLd = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: "https://raileon.com",
  name: "Raileon",
  publisher: { "@id": ORGANIZATION_ID },
};
